import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Sun } from "lucide-react";
import { toast } from "sonner";

const DAYS_OF_WEEK = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Daily"];

export default function AdorationScheduleDialog({ onSave, editData = null, trigger }) {
  const [open, setOpen] = useState(false);
  const [schedule, setSchedule] = useState({
    day: "Thursday",
    start_time: "",
    end_time: "",
    perpetual: false,
    notes: ""
  });

  useEffect(() => {
    if (open && editData) {
      setSchedule(editData);
    } else if (open && !editData) {
      setSchedule({ day: "Thursday", start_time: "", end_time: "", perpetual: false, notes: "" });
    }
  }, [open, editData]);

  const handleSave = () => {
    if (!schedule.start_time || !schedule.end_time) {
      toast.error("Please enter start and end times");
      return;
    }
    onSave(schedule);
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger}
      </DialogTrigger>
      <DialogContent className="bg-white border-[#E5E0D5] max-w-md">
        <DialogHeader>
          <DialogTitle className="font-['Crimson_Text'] text-xl text-[#2D2A26] flex items-center gap-2">
            <Sun className="w-5 h-5 text-[#D4AF37]" /> {editData ? "Edit" : "Add"} Adoration Schedule
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs font-['Nunito']">Day</Label>
            <Select value={schedule.day} onValueChange={(v) => setSchedule({...schedule, day: v})}>
              <SelectTrigger className="border-[#E5E0D5] text-sm font-['Nunito']">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white">
                {DAYS_OF_WEEK.map(d => <SelectItem key={d} value={d} className="font-['Nunito'] text-sm">{d}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-['Nunito']">Start Time</Label>
              <Input 
                value={schedule.start_time} 
                onChange={(e) => setSchedule({...schedule, start_time: e.target.value})}
                placeholder="5:00 PM"
                className="border-[#E5E0D5] font-['Nunito'] text-sm"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-['Nunito']">End Time</Label>
              <Input 
                value={schedule.end_time} 
                onChange={(e) => setSchedule({...schedule, end_time: e.target.value})}
                placeholder="7:00 PM"
                className="border-[#E5E0D5] font-['Nunito'] text-sm"
              />
            </div>
          </div>
          <div className="flex items-center justify-between p-3 bg-[#F9F7F2] rounded-md">
            <div>
              <Label className="text-sm font-['Nunito'] font-semibold">Perpetual Adoration</Label>
              <p className="text-xs text-[#6B665E] font-['Nunito']">24/7 adoration chapel</p>
            </div>
            <Switch 
              checked={schedule.perpetual}
              onCheckedChange={(v) => setSchedule({...schedule, perpetual: v})}
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-['Nunito']">Notes (optional)</Label>
            <Input 
              value={schedule.notes} 
              onChange={(e) => setSchedule({...schedule, notes: e.target.value})}
              placeholder="e.g., In the chapel, First Friday"
              className="border-[#E5E0D5] font-['Nunito'] text-sm"
            />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={handleSave} className="bg-[#D4AF37] hover:bg-[#B8942E] text-white font-['Nunito']">
            {editData ? "Save Changes" : "Add Schedule"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
